import {
  createContext,
  useContext,
  ReactNode,
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { useWhatsappEvents } from '../hooks/WAHA/useWhatsappEvents';
import { WAHAWebhookEvent, WAHAEventType } from '../services/interface';

import { Message } from '../features/messages/messages.types';
import {
  getConversation,
  getConversationsList,
} from '@/features/conversations/conversations.service';
import { fetchChannel } from '@/features/channels/channels.service';
import { CreateResult, Identifier } from 'react-admin';

import { Conversation } from '@/features/conversations/conversations.types';

// Enhanced provider props

// Add a lock mechanism for production environments
const eventProcessingLocks = new Map<string, Promise<void>>();

interface WhatsAppEventsProviderProps {
  children: ReactNode;
  sessionName?: string;
  maxStoredEvents?: number;
  // New configuration options from environment variables
  wsUrl?: string;
  reconnectMaxAttempts?: number;
  initialReconnectDelay?: number;
  maxReconnectDelay?: number;
}

interface ChannelConnection {
  channelId: string;
  isConnected: boolean;
  lastEvent?: WAHAWebhookEvent;
  subscribers: Map<string, (event: WAHAWebhookEvent) => void>;
  // New property to track the connection instance
  connectionHook?: ReturnType<typeof useWhatsappEvents>;
}

interface WhatsAppEventsContextProps {
  isConnected: boolean;
  lastEvent: WAHAWebhookEvent | null;
  subscribe: (
    eventType: WAHAEventType | 'all',
    handler: (event: WAHAWebhookEvent) => void,
  ) => string;
  unsubscribe: (id: string) => void;
  messages: WAHAWebhookEvent[];
  statusEvents: WAHAWebhookEvent[];
  incomingMessages: Message[];
  clearEvents: () => void;
  reconnectWebSocket: () => void;
  connectionAttempts: number;
  // New channel-specific functionality
  activeChannels: string[];
  connectChannel: (channelId: string) => Promise<void>;
  disconnectChannel: (channelId: string) => void;
  isChannelConnected: (channelId: string) => boolean;
  subscribeToChannel: (
    channelId: string,
    eventType: WAHAEventType | 'all',
    handler: (event: WAHAWebhookEvent) => void,
  ) => string;
  processIncomingMessage: (event: WAHAWebhookEvent) => Promise<
    | CreateResult<
        Message & {
          id: Identifier;
        }
      >
    | undefined
  >;
  // New properties for selected conversation
  currentSelectedConversationId: string | null;
  setSelectedConversationIdForContext: (id: string | null) => void;
}

const WhatsAppEventsContext = createContext<
  WhatsAppEventsContextProps | undefined
>(undefined);

// Create a component to hold a channel-specific connection
function ChannelConnection({
  channelId,
  onEvent,
}: {
  channelId: string;
  onEvent: (event: WAHAWebhookEvent) => void;
}) {
  // Get the actual session ID from the channel configuration
  const [sessionId, setSessionId] = useState<string | null>(null);
  
  useEffect(() => {
    const fetchChannelAndExtractSessionId = async () => {
      try {
        const channel = await fetchChannel({ id: channelId });
        if (channel?.data?.channel_config) {
          const config = typeof channel.data.channel_config === 'string' 
            ? JSON.parse(channel.data.channel_config)
            : channel.data.channel_config;
          
          const extractedSessionId = config.sessionId || 
            (config.whatsapp && config.whatsapp.sessionName) ||
            channelId; // fallback to channelId if no sessionId found
          
          console.log(`[ChannelConnection] Using session ID: ${extractedSessionId} for channel: ${channelId}`);
          setSessionId(extractedSessionId);
        } else {
          console.warn(`[ChannelConnection] No channel config found for channel: ${channelId}, using channelId as fallback`);
          setSessionId(channelId);
        }
      } catch (error) {
        console.error(`[ChannelConnection] Failed to fetch channel ${channelId}:`, error);
        setSessionId(channelId); // fallback
      }
    };
    
    fetchChannelAndExtractSessionId();
  }, [channelId]);

  // Use the extracted session ID for the WebSocket connection
  const connection = useWhatsappEvents(sessionId || channelId, {
    wsUrl: import.meta.env.VITE_WHATSAPP_SERVER_WEBSOCKET_URL,
    reconnectConfig: {
      maxAttempts: Number(
        import.meta.env.VITE_WHATSAPP_RECONNECT_MAX_ATTEMPTS || 10,
      ),
      initialDelay: Number(
        import.meta.env.VITE_WHATSAPP_INITIAL_RECONNECT_DELAY || 1000,
      ),
      maxDelay: Number(
        import.meta.env.VITE_WHATSAPP_MAX_RECONNECT_DELAY || 30000,
      ),
    },
  });

  // Forward all events to the parent handler
  useEffect(() => {
    if (connection.lastEvent) {
      console.log(
        `[Channel ${channelId}] Received event:`,
        connection.lastEvent,
      );
      onEvent(connection.lastEvent);
    }
  }, [connection.lastEvent, channelId, onEvent]);

  // Return connection status for debugging
  return null;
}

// Updated function to return an array of possible formats
export function normalizeWhatsAppNumber(phoneNumber?: string): string[] {
  if (!phoneNumber) return [];

  // Remove @c.us or @s.whatsapp.net
  let normalized = phoneNumber.replace(/@c\.us|@s\.whatsapp\.net/g, '');

  // Remove any non-digit characters except a leading +
  normalized = normalized.replace(/[^\d+]/g, '');

  // Create variations with and without + prefix
  const formats = [];

  // Current format
  formats.push(normalized);

  // If it has a + prefix, add version without it
  if (normalized.startsWith('+')) {
    formats.push(normalized.substring(1));
  }
  // If it doesn't have a + prefix, add version with it
  else {
    formats.push(`+${normalized}`);
  }

  return formats;
}

// Add a global event registry to prevent duplicate processing
const globalProcessedEvents = new Map<
  string,
  { timestamp: number; processed: boolean }
>();

// Clean up old processed events (older than 5 minutes) to prevent memory leaks
function cleanupOldProcessedEvents() {
  const fiveMinutesAgo = Date.now() - 5 * 60 * 1000;
  for (const [eventId, eventData] of globalProcessedEvents.entries()) {
    if (eventData.timestamp < fiveMinutesAgo) {
      globalProcessedEvents.delete(eventId);
    }
  }
}

// Run cleanup every minute
setInterval(cleanupOldProcessedEvents, 60 * 1000);

// Check if an event has already been processed globally
function isEventAlreadyProcessed(eventId: string): boolean {
  const eventData = globalProcessedEvents.get(eventId);
  return eventData?.processed || false;
}

// Create a lock for event processing to handle race conditions
async function acquireEventLock(eventId: string): Promise<boolean> {
  // Check if event is already being processed
  if (eventProcessingLocks.has(eventId)) {
    console.log(
      `[acquireEventLock] Event ${eventId} is already being processed`,
    );
    return false; // Another process handled it
  }

  // Create a new lock
  const lockPromise = new Promise<void>((resolve) => {
    // Store the resolve function to call later
    setTimeout(resolve, 5000); // Auto-release after 5 seconds
  });

  eventProcessingLocks.set(eventId, lockPromise);

  // Return a function to release the lock
  return true;
}

function releaseEventLock(eventId: string) {
  eventProcessingLocks.delete(eventId);
}

export function WhatsAppEventsProvider({
  children,
  sessionName = 'default',
  maxStoredEvents = 100,
  // Default to environment variables or fallback values
  wsUrl = import.meta.env.VITE_WHATSAPP_SERVER_WEBSOCKET_URL,
  reconnectMaxAttempts = Number(
    import.meta.env.VITE_WHATSAPP_RECONNECT_MAX_ATTEMPTS || 10,
  ),
  initialReconnectDelay = Number(
    import.meta.env.VITE_WHATSAPP_INITIAL_RECONNECT_DELAY || 1000,
  ),
  maxReconnectDelay = Number(
    import.meta.env.VITE_WHATSAPP_MAX_RECONNECT_DELAY || 30000,
  ),
}: WhatsAppEventsProviderProps) {
  // Default connection for global events
  const { isConnected, lastEvent, subscribe, unsubscribe, reconnect } =
    useWhatsappEvents(sessionName, {
      wsUrl,
      reconnectConfig: {
        maxAttempts: reconnectMaxAttempts,
        initialDelay: initialReconnectDelay,
        maxDelay: maxReconnectDelay,
      },
    });

  const [messages, setMessages] = useState<WAHAWebhookEvent[]>([]);
  const [statusEvents, setStatusEvents] = useState<WAHAWebhookEvent[]>([]);
  const [connectionAttempts, setConnectionAttempts] = useState(0);
  const [activeChannels, setActiveChannels] = useState<string[]>([]);
  const [currentSelectedConversationId, setCurrentSelectedConversationId] =
    useState<string | null>(null);
  const [incomingMessages] = useState<Message[]>([]);

  // Track channel-specific connections
  const channelConnectionsRef = useRef<Map<string, ChannelConnection>>(
    new Map(),
  );
  // Track which channels have active React components
  const [activeChannelComponents, setActiveChannelComponents] = useState<
    string[]
  >([]);

  // Function to manually trigger reconnection
  const reconnectWebSocket = useCallback(() => {
    if (reconnect) {
      reconnect();
      setConnectionAttempts(0);
    }
  }, [reconnect]);

  // Connect to a specific channel - create a new connection for this channel
  const connectChannel = useCallback(async (channelId: string) => {
    try {
      console.log(
        `[connectChannel] Attempting to connect to channel: ${channelId}`,
      );

      // Get channel details from the database
      const channel = await fetchChannel({ id: channelId });

      if (!channel) {
        throw new Error(`Channel ${channelId} not found`);
      }
      console.log('channel **********', channel);
      // Only proceed if this is a WhatsApp channel
      if (channel.data.type !== 'whatsapp') {
        console.log(
          `Channel ${channelId} is not a WhatsApp channel, type: ${channel.data.type}`,
        );
        return;
      }

      // Create or get channel connection
      let connection = channelConnectionsRef.current.get(channelId);

      if (!connection) {
        connection = {
          channelId,
          isConnected: false,
          subscribers: new Map(),
        };
        channelConnectionsRef.current.set(channelId, connection);
      }

      // Add to active channels
      setActiveChannels((prev) => {
        if (!prev.includes(channelId)) {
          return [...prev, channelId];
        }
        return prev;
      });

      // Add to active components to create a WebSocket connection
      setActiveChannelComponents((prev) => {
        if (!prev.includes(channelId)) {
          return [...prev, channelId];
        }
        return prev;
      });

      // Mark as connected
      connection.isConnected = true;
      console.log(
        `[connectChannel] Successfully connected to channel: ${channelId}`,
      );
    } catch (error) {
      console.error(
        `[connectChannel] Failed to connect to channel ${channelId}:`,
        error,
      );
      throw error;
    }
  }, []);

  // Disconnect from a specific channel
  const disconnectChannel = useCallback((channelId: string) => {
    const connection = channelConnectionsRef.current.get(channelId);
    if (connection) {
      console.log(
        `[disconnectChannel] Disconnecting from channel: ${channelId}`,
      );
      connection.isConnected = false;
      connection.subscribers.clear();

      // Remove from active components
      setActiveChannelComponents((prev) =>
        prev.filter((id) => id !== channelId),
      );

      // Remove from active channels list
      setActiveChannels((prev) => prev.filter((id) => id !== channelId));

      // Delete from map
      channelConnectionsRef.current.delete(channelId);
    }
  }, []);

  // Check if a specific channel is connected
  const isChannelConnected = useCallback((channelId: string) => {
    const connection = channelConnectionsRef.current.get(channelId);
    return connection?.isConnected || false;
  }, []);

  // Handle events from channel-specific connections
  const handleChannelEvent = useCallback(
    async (channelId: string, event: WAHAWebhookEvent) => {
      console.log(
        `[handleChannelEvent] Received event for channel ${channelId}:`,
        event,
      );

      // Filter out message.any events immediately
      if (event.event === 'message.any') {
        console.log(
          `[handleChannelEvent] Ignoring message.any event: ${event.id}`,
        );
        return;
      }

      // Try to acquire a lock for this event
      const hasLock = await acquireEventLock(event.id);
      console.log('handleChannelEvent - hasLock', hasLock);
      if (!hasLock) {
        console.log(
          `[handleChannelEvent] Event ${event.id} is being processed by another handler`,
        );
        return;
      }

      try {
        // Check if this event has already been processed globally
        if (isEventAlreadyProcessed(event.id)) {
          console.log(
            `[handleChannelEvent] Event ${event.id} already processed globally, skipping`,
          );
          return;
        }

        const connection = channelConnectionsRef.current.get(channelId);

        if (connection) {
          connection.lastEvent = event;

          // For non-message events, notify subscribers immediately
          if (event.event !== 'message') {
            connection.subscribers.forEach((handler) => {
              try {
                handler(event);
              } catch (error) {
                console.error(
                  `[handleChannelEvent] Error in subscriber for channel ${channelId}:`,
                  error,
                );
              }
            });
          }

          // Process message event for saving to database (ONLY HERE)
          if (event.event === 'message') {
            // Early return if we already have this message ID in our processed events
            if (globalProcessedEvents.has(event.id)) {
              console.log(
                `[handleChannelEvent] Event ${event.id} already processed, skipping`,
              );
              return;
            }

            // NOTE: We use the channelId parameter passed to handleChannelEvent (database UUID),
            // NOT event.session (which is the WAHA session name) for database lookups
            const incomingChatId = event.payload.from; // The raw WhatsApp chat ID (e.g., "31640646189@c.us")
            const possiblePhoneFormats =
              normalizeWhatsAppNumber(incomingChatId);

            if (!incomingChatId) {
              console.error(
                '[processIncomingMessage] No "from" field in message payload:',
                event.payload,
              );
              return;
            }

            console.log(
              `[handleChannelEvent] Looking for conversation with channel_id: ${channelId}, wab_chatId_external: ${incomingChatId}`,
            );

            let targetConversation: Conversation | null = null;

            // 1. FIRST try to find by wab_chatId_external
            try {
              const { data: chatIdMatches } = await getConversationsList({
                filter: {
                  channel_id: channelId,
                  wab_chatId_external: incomingChatId,
                },
                perPage: 1,
                page: 1,
                sort: 'created_at',
                order: 'DESC',
              });

              if (chatIdMatches && chatIdMatches.length > 0) {
                targetConversation = chatIdMatches[0];
                console.log(
                  `[processIncomingMessage] Found conversation by wab_chatId_external: ${targetConversation.id}`,
                );
              }
            } catch (error) {
              console.warn(
                `[processIncomingMessage] Error looking up by wab_chatId_external: ${error}`,
              );
              // Continue to other lookup methods
            }

            // 2. If not found, try to find by normalized phone numbers
            if (!targetConversation) {
              console.log(
                `[processIncomingMessage] No match by wab_chatId_external, trying phone number match...`,
              );

              const { data: conversationsForChannel } =
                await getConversationsList({
                  filter: { channel_id: channelId },
                  perPage: 200,
                  page: 1,
                  sort: 'updated_at',
                  order: 'DESC',
                });

              // Look through all conversations for a match on phone number
              for (const conv of conversationsForChannel) {
                if (!conv.user?.phone_number) continue;

                const convPhoneFormats = normalizeWhatsAppNumber(
                  conv.user.phone_number,
                );

                // Check if any format matches
                const matchFound = possiblePhoneFormats.some((phoneFormat) =>
                  convPhoneFormats.some(
                    (convFormat) => convFormat === phoneFormat,
                  ),
                );

                if (matchFound) {
                  targetConversation = conv;
                  console.log(
                    `[processIncomingMessage] Found conversation by phone number match: ${conv.id}`,
                  );
                  break;
                }
              }
            }

            // 3. If still not found, and we have a selected conversation, try that
            if (!targetConversation && currentSelectedConversationId) {
              console.log(
                `[processIncomingMessage] No direct match, checking selected conversation: ${currentSelectedConversationId}`,
              );
              try {
                const { data: selectedConv } = await getConversation({
                  id: currentSelectedConversationId,
                });

                // Check if the selected conversation is for the right channel
                if (selectedConv && selectedConv.channel_id === channelId) {
                  // Either match the wab_chatId_external or the phone number
                  if (selectedConv.wab_chatId_external === incomingChatId) {
                    targetConversation = selectedConv;
                    console.log(
                      `[processIncomingMessage] Matched with currently selected conversation by wab_chatId_external: ${selectedConv.id}`,
                    );
                  } else if (selectedConv.user?.phone_number) {
                    const selectedConvPhoneFormats = normalizeWhatsAppNumber(
                      selectedConv.user.phone_number,
                    );
                    const matchFound = possiblePhoneFormats.some(
                      (phoneFormat) =>
                        selectedConvPhoneFormats.some(
                          (convFormat) => convFormat === phoneFormat,
                        ),
                    );

                    if (matchFound) {
                      targetConversation = selectedConv;
                      console.log(
                        `[processIncomingMessage] Matched with currently selected conversation by phone number: ${selectedConv.id}`,
                      );
                    }
                  }
                }

                if (!targetConversation) {
                  console.log(
                    `[processIncomingMessage] Selected conversation ${currentSelectedConversationId} not a match`,
                  );
                }
              } catch (error) {
                console.error(
                  `[processIncomingMessage] Error fetching selected conversation ${currentSelectedConversationId}:`,
                  error,
                );
              }
            }

            // 4. If conversation not found, backend webhook will handle creation
            // Just mark as processed and notify subscribers for UI refresh
            if (!targetConversation) {
              console.log(
                `[handleChannelEvent] No existing conversation found for ${incomingChatId}. Backend webhook will handle user/conversation/message creation.`,
              );
            }

            // Mark this event as processed globally
            globalProcessedEvents.set(event.id, {
              timestamp: Date.now(),
              processed: true,
            });

            // Notify subscribers for UI updates (they will invalidate queries to refresh data)
            // Backend webhook handles all data creation - frontend only refreshes UI
            connection.subscribers.forEach((handler) => {
              try {
                handler(event);
              } catch (error) {
                console.error(
                  `[handleChannelEvent] Error in subscriber for channel ${channelId}:`,
                  error,
                );
              }
            });

            console.log(
              `[handleChannelEvent] Notified ${connection.subscribers.size} subscribers for channel ${channelId}`,
            );
          }
        }
      } finally {
        // Always release the lock
        releaseEventLock(event.id);
      }
    },
    [currentSelectedConversationId],
  );

  // Subscribe to events for a specific channel
  const subscribeToChannel = useCallback(
    (
      channelId: string,
      eventType: WAHAEventType | 'all',
      handler: (event: WAHAWebhookEvent) => void,
    ) => {
      const connection = channelConnectionsRef.current.get(channelId);
      if (!connection) {
        console.error(
          `[subscribeToChannel] Cannot subscribe to channel ${channelId}: not connected`,
        );
        throw new Error(`Channel ${channelId} not connected`);
      }

      const subscriptionId = `${channelId}-${Date.now()}-${Math.random()}`;

      // Create a handler that filters by event type
      const eventHandler = (event: WAHAWebhookEvent) => {
        if (eventType === 'all' || event.event === eventType) {
          handler(event);
        }
      };

      connection.subscribers.set(subscriptionId, eventHandler);
      console.log(
        `[subscribeToChannel] Subscribed to channel ${channelId} with ID: ${subscriptionId}`,
      );

      return subscriptionId;
    },
    [],
  );

  // Process incoming messages - simplified version that lets backend handle all creation
  // This function is exposed via context for external use, but all user/conversation/message
  // creation is now handled by the backend via webhooks
  const processIncomingMessage = useCallback(
    async (event: WAHAWebhookEvent) => {
      try {
        console.log('[processIncomingMessage] Processing event:', {
          type: event.event,
          session: event.session,
          payload: event.payload,
        });

        if (event.event === 'message' && event.payload) {
          // Early return if we already have this message ID in our processed events
          if (globalProcessedEvents.has(event.id)) {
            console.log(
              `[processIncomingMessage] Event ${event.id} already processed, skipping`,
            );
            return;
          }

          // Mark as processed - backend webhook handles all user/conversation/message creation
          globalProcessedEvents.set(event.id, {
            timestamp: Date.now(),
            processed: true,
          });

          console.log(
            `[processIncomingMessage] Event marked as processed. Backend webhook handles data creation for: ${event.payload.from}`,
          );
        }
      } catch (error) {
        console.error('[processIncomingMessage] Error:', error);
      }
    },
    [],
  );


  // Update default messages and status events
  useEffect(() => {
    if (lastEvent) {
      if (lastEvent.event === 'message') {
        setMessages((prev) => {
          const newMessages = [lastEvent, ...prev];
          return newMessages.slice(0, maxStoredEvents);
        });
      } else if (lastEvent.event === 'session.status') {
        setStatusEvents((prev) => {
          const newEvents = [lastEvent, ...prev];
          return newEvents.slice(0, maxStoredEvents);
        });
      }
    }
  }, [lastEvent, maxStoredEvents]);

  // Update connection attempts counter
  useEffect(() => {
    if (!isConnected) {
      setConnectionAttempts((prev) => prev + 1);
    } else {
      setConnectionAttempts(0);
    }
  }, [isConnected]);

  const clearEvents = useCallback(() => {
    setMessages([]);
    setStatusEvents([]);
  }, []);

  const setSelectedConversationIdForContext = useCallback(
    (id: string | null) => {
      setCurrentSelectedConversationId(id);
    },
    [],
  );

  const value = useMemo(
    () => ({
      isConnected,
      lastEvent,
      subscribe,
      unsubscribe,
      messages,
      statusEvents,
      incomingMessages,
      clearEvents,
      reconnectWebSocket,
      connectionAttempts,
      activeChannels,
      connectChannel,
      disconnectChannel,
      isChannelConnected,
      subscribeToChannel,
      processIncomingMessage,
      currentSelectedConversationId,
      setSelectedConversationIdForContext,
    }),
    [
      isConnected,
      lastEvent,
      subscribe,
      unsubscribe,
      messages,
      statusEvents,
      incomingMessages,
      clearEvents,
      reconnectWebSocket,
      connectionAttempts,
      activeChannels,
      connectChannel,
      disconnectChannel,
      isChannelConnected,
      subscribeToChannel,
      processIncomingMessage,
      currentSelectedConversationId,
      setSelectedConversationIdForContext,
    ],
  );

  return (
    <WhatsAppEventsContext.Provider value={value}>
      {children}

      {/* Render a connection component for each active channel */}
      {activeChannelComponents.map((channelId) => (
        <ChannelConnection
          key={channelId}
          channelId={channelId}
          onEvent={(event) => handleChannelEvent(channelId, event)}
        />
      ))}
    </WhatsAppEventsContext.Provider>
  );
}

export function useWhatsAppEvents() {
  const context = useContext(WhatsAppEventsContext);
  if (context === undefined) {
    throw new Error(
      'useWhatsAppEvents must be used within a WhatsAppEventsProvider',
    );
  }
  return context;
}
